import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { DownloadSimple, Trash, Warning } from "@phosphor-icons/react";

export default function AccountData() {
  const [confirm, setConfirm] = useState("");
  const [mode, setMode] = useState("soft");
  const [deleting, setDeleting] = useState(false);

  const download = async (what) => {
    try {
      const token = localStorage.getItem("netcrm_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account/export/${what}.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${what}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${what}`);
    } catch (e) { toast.error(e.message || "export failed"); }
  };

  const deleteAccount = async () => {
    if (!confirm.trim()) return;
    if (!window.confirm(`DELETE ACCOUNT (${mode})?\n\nThis cannot be undone when 'hard' is selected.`)) return;
    setDeleting(true);
    try {
      await api.post("/account/delete", { confirmation: confirm, mode });
      toast.success("Account scheduled for deletion. You will be signed out.");
      localStorage.removeItem("netcrm_token");
      setTimeout(() => { window.location.href = "/login"; }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="account-data-page">
      <h1 className="text-3xl lg:text-4xl font-bold">// your data</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Export everything you own, or delete your account. We honour this fully.</p>

      <section className="border p-6 mt-6" style={{ borderColor: "var(--border)" }} data-testid="export-section">
        <div className="overline mb-3 flex items-center gap-2"><DownloadSimple weight="bold" /> // export</div>
        <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>Download a CSV copy of your records anytime.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => download("contacts")} className="btn-primary flex items-center gap-2" data-testid="export-contacts-btn">
            <DownloadSimple weight="bold" /> Contacts CSV
          </button>
          <button onClick={() => download("tasks")} className="btn-secondary flex items-center gap-2" data-testid="export-tasks-btn">
            <DownloadSimple weight="bold" /> Tasks CSV
          </button>
          <button onClick={() => download("activities")} className="btn-secondary flex items-center gap-2" data-testid="export-activities-btn">
            <DownloadSimple weight="bold" /> Activity CSV
          </button>
        </div>
      </section>

      <section className="border p-6 mt-6" style={{ borderColor: "#FF3B30" }} data-testid="delete-section">
        <div className="overline mb-3 flex items-center gap-2" style={{ color: "#FF3B30" }}><Warning weight="bold" /> // delete account</div>
        <p className="text-sm mb-3" style={{ color: "var(--text-2)" }}>
          <strong>Soft delete</strong>: your account is disabled and data is retained for 30 days in case you change your mind.
          <br />
          <strong>Hard delete</strong>: everything (contacts, campaigns, drafts) is irreversibly removed.
        </p>
        <div className="flex items-center gap-3 mb-3">
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="px-3 py-2 border bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="delete-mode-select">
            <option value="soft">soft (recoverable 30d)</option>
            <option value="hard">hard (permanent)</option>
          </select>
        </div>
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="type your account email to confirm"
          className="w-full px-3 py-2 border bg-transparent mb-3"
          style={{ borderColor: "var(--border)" }}
          data-testid="delete-confirm-input"
        />
        <button
          onClick={deleteAccount}
          disabled={!confirm || deleting}
          className="flex items-center gap-2 disabled:opacity-40"
          style={{ background: "#FF3B30", color: "white", padding: "10px 16px", border: "1px solid #FF3B30" }}
          data-testid="delete-account-btn"
        >
          <Trash weight="bold" /> {deleting ? "Deleting…" : "Delete account"}
        </button>
      </section>
    </div>
  );
}
